// src/components/settings/shared/BasicInfoTab.jsx
import React, { useState } from 'react';
import {
  Upload,
  X,
  Image as ImageIcon,
  User,
  Building,
  Mail,
  Phone,
  Globe,
  MapPin,
  Loader2,
  CheckCircle,
  AlertCircle,
  Info,
  Camera,
  Store,
  Calendar,
  Users,
  Package,
  School,
  Heart,
  Bed,
  Clock,
  Car,
  Building2,
  Tag,
  Briefcase
} from 'lucide-react';
import { FileUpload } from '../../common/FileUpload';

// ============================================================================
// TEMPLATE CATEGORIES CONFIGURATION
// ============================================================================

const TEMPLATE_CATEGORIES = {
  album: {
    label: 'Album Type',
    description: 'Choose the type of album that best fits your content',
    categories: {
      'photo-album': {
        label: 'Photo Album',
        icon: Camera,
        color: '#10b981',
        description: 'Traditional photo gallery for memories and events'
      },
      'menu': {
        label: 'Menu',
        icon: Building,
        color: '#f59e0b',
        description: 'Restaurant menu with dishes, prices, and descriptions'
      },
      'listing': {
        label: 'Listing',
        icon: Building2,
        color: '#3b82f6',
        description: 'Property or service listings with details and pricing'
      },
      'store': {
        label: 'Store Catalog',
        icon: Store,
        color: '#8b5cf6',
        description: 'Product catalog with shopping features'
      },
      'portfolio': {
        label: 'Portfolio',
        icon: Briefcase,
        color: '#ec4899',
        description: 'Professional work showcase and portfolio'
      },
      'gallery': {
        label: 'Art Gallery',
        icon: ImageIcon,
        color: '#06b6d4',
        description: 'Art exhibition and gallery display'
      }
    }
  },

  profile: {
    label: 'Profile Type',
    description: 'Select the type of profile you want to create',
    categories: {
      'personal': {
        label: 'Personal Profile',
        icon: User,
        color: '#8b5cf6',
        description: 'Individual personal profile or resume'
      },
      'professional': {
        label: 'Professional',
        icon: Briefcase,
        color: '#3b82f6',
        description: 'Professional CV or business profile'
      },
      'portfolio': {
        label: 'Portfolio',
        icon: Package,
        color: '#10b981',
        description: 'Creative portfolio and work showcase'
      },
      'business': {
        label: 'Business Profile',
        icon: Building,
        color: '#f59e0b',
        description: 'Company or business profile page'
      },
      'product': {
        label: 'Product Profile',
        icon: Package,
        color: '#ec4899',
        description: 'Product specification and details'
      },
      'service': {
        label: 'Service Provider',
        icon: Users,
        color: '#14b8a6',
        description: 'Service provider or freelancer profile'
      }
    }
  },

  event: {
    label: 'Event Type',
    description: 'What type of event are you organizing?',
    categories: {
      'social': {
        label: 'Social Event',
        icon: Users,
        color: '#10b981',
        description: 'Birthday parties, casual gatherings, celebrations'
      },
      'wedding': {
        label: 'Wedding',
        icon: Heart,
        color: '#f472b6',
        description: 'Wedding ceremonies and receptions'
      },
      'reunion': {
        label: 'Reunion',
        icon: Users,
        color: '#8b5cf6',
        description: 'School, family, or group reunions'
      },
      'seminar': {
        label: 'Seminar/Workshop',
        icon: Briefcase,
        color: '#3b82f6',
        description: 'Educational seminars and training sessions'
      },
      'conference': {
        label: 'Conference',
        icon: Building2,
        color: '#059669',
        description: 'Professional conferences and conventions'
      },
      'fundraiser': {
        label: 'Fundraiser',
        icon: Heart,
        color: '#dc2626',
        description: 'Charity events and fundraising campaigns'
      },
      'corporate': {
        label: 'Corporate Event',
        icon: Building,
        color: '#6366f1',
        description: 'Business meetings and company events'
      }
    }
  },

  booking: {
    label: 'Booking Type',
    description: 'What type of booking system do you need?',
    categories: {
      'accommodation': {
        label: 'Accommodation',
        icon: Bed,
        color: '#3b82f6',
        description: 'Hotels, B&Bs, vacation rentals, and lodging'
      },
      'appointment': {
        label: 'Appointments',
        icon: Clock,
        color: '#10b981',
        description: 'Medical, beauty, professional services'
      },
      'rental': {
        label: 'Rentals',
        icon: Car,
        color: '#f59e0b',
        description: 'Vehicle rentals, equipment hire'
      },
      'venue': {
        label: 'Venues',
        icon: Building2,
        color: '#8b5cf6',
        description: 'Event spaces, halls, meeting rooms'
      }
    }
  },

  ecommerce: {
    label: 'Store Type',
    description: 'What type of products or services do you sell?',
    categories: {
      'general': {
        label: 'General Store',
        icon: Store,
        color: '#3b82f6',
        description: 'Multi-category online store'
      },
      'restaurant': {
        label: 'Restaurant/Food',
        icon: Building,
        color: '#f59e0b',
        description: 'Food delivery and restaurant ordering'
      },
      'services': {
        label: 'Service Business',
        icon: Users,
        color: '#10b981',
        description: 'Service booking and appointments'
      },
      'digital': {
        label: 'Digital Products',
        icon: Package,
        color: '#8b5cf6',
        description: 'Software, courses, digital downloads'
      },
      'rental': {
        label: 'Rental/Booking',
        icon: Clock,
        color: '#ef4444',
        description: 'Equipment rental and property booking'
      },
      'marketplace': {
        label: 'Marketplace',
        icon: Store,
        color: '#14b8a6',
        description: 'Multi-vendor marketplace platform'
      }
    }
  },

  institution: {
    label: 'Institution Type',
    description: 'What type of educational institution?',
    categories: {
      'school': {
        label: 'School',
        icon: School,
        color: '#3b82f6',
        description: 'Primary, middle, or high school'
      },
      'university': {
        label: 'University/College',
        icon: Building,
        color: '#8b5cf6',
        description: 'Higher education institution'
      },
      'training': {
        label: 'Training Center',
        icon: Users,
        color: '#10b981',
        description: 'Professional training and courses'
      },
      'academy': {
        label: 'Academy',
        icon: School,
        color: '#f59e0b',
        description: 'Specialized academy or institute'
      }
    }
  },

  organisation: {
    label: 'Organisation Type',
    description: 'What type of organization?',
    categories: {
      'company': {
        label: 'Company',
        icon: Building,
        color: '#3b82f6',
        description: 'Business or corporation'
      },
      'nonprofit': {
        label: 'Non-Profit',
        icon: Heart,
        color: '#10b981',
        description: 'Non-profit organization or charity'
      },
      'government': {
        label: 'Government',
        icon: Building2,
        color: '#6366f1',
        description: 'Government agency or department'
      },
      'community': {
        label: 'Community',
        icon: Users,
        color: '#f59e0b',
        description: 'Community group or association'
      }
    }
  },

  catalog: {
    label: 'Catalog Type',
    description: 'What type of catalog are you creating?',
    categories: {
      'general': {
        label: 'General Catalog',
        icon: Package,
        color: '#3b82f6',
        description: 'General product or service catalog'
      },
      'menu': {
        label: 'Menu',
        icon: Building,
        color: '#f59e0b',
        description: 'Restaurant or food menu'
      },
      'services': {
        label: 'Services',
        icon: Briefcase,
        color: '#10b981',
        description: 'Service offerings catalog'
      },
      'products': {
        label: 'Products',
        icon: Store,
        color: '#8b5cf6',
        description: 'Product showcase catalog'
      }
    }
  }
};

// ============================================================================
// OWNERSHIP TYPES
// ============================================================================

const OWNERSHIP_TYPES = {
  individual: {
    label: 'Individual',
    icon: User,
    description: 'Owned by an individual person',
    fields: ['ownerName', 'ownerEmail', 'ownerPhone']
  },
  organisation: {
    label: 'Organisation',
    icon: Building,
    description: 'Owned by a company or organization',
    fields: ['organisationName', 'organisationEmail', 'organisationPhone', 'organisationWebsite']
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const BasicInfoTab = ({
  formData,
  onInputChange,
  onFileUpload,
  uploadStates = {},
  errors = {},
  templateType,
  templateConfig
}) => {
  const [activeOwnershipType, setActiveOwnershipType] = useState(
    formData.ownershipType || 'individual'
  );

  // Get template-specific category configuration
  const categoryConfig = TEMPLATE_CATEGORIES[templateType];
  const currentCategory = formData[`${templateType}Category`] || 
                         formData.albumCategory || 
                         formData.profileType || 
                         formData.eventCategory || 
                         formData.bookingType || 
                         formData.storeType ||
                         formData.institutionType ||
                         formData.organizationType ||
                         formData.catalogType ||
                         Object.keys(categoryConfig?.categories || {})[0];

  // Get template-specific naming labels
  const getNameLabel = () => {
    const labels = {
      album: 'Album Name',
      profile: 'Profile Name',
      event: 'Event Name',
      booking: 'Business Name',
      ecommerce: 'Store Name',
      institution: 'Institution Name',
      organisation: 'Organisation Name',
      catalog: 'Catalog Name'
    };
    return labels[templateType] || 'Name';
  };

  const getDescriptionLabel = () => {
    const labels = {
      album: 'Album Description',
      profile: 'About',
      event: 'Event Description',
      booking: 'Business Description',
      ecommerce: 'Store Description',
      institution: 'Institution Overview',
      organisation: 'Organisation Overview',
      catalog: 'Catalog Description'
    };
    return labels[templateType] || 'Description';
  };

  const handleOwnershipTypeChange = (type) => {
    setActiveOwnershipType(type);
    onInputChange('ownershipType', type);
  };

  const handleCategoryChange = (categoryKey) => {
    // Determine the correct field name based on template type
    const categoryFieldMap = {
      album: 'albumCategory',
      profile: 'profileType',
      event: 'eventCategory',
      booking: 'bookingType',
      ecommerce: 'storeType',
      institution: 'institutionType',
      organisation: 'organizationType',
      catalog: 'catalogType'
    };

    const fieldName = categoryFieldMap[templateType];
    if (fieldName) {
      onInputChange(fieldName, categoryKey);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Basic Information
        </h3>
        <p className="text-sm text-gray-600">
          Essential details about your {templateConfig?.label?.toLowerCase() || 'template'}
        </p>
      </div>

      {/* ========================================================================
          TEMPLATE NAMING SECTION
      ======================================================================== */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-6">
        <div className="flex items-center gap-2 pb-4 border-b border-gray-200">
          <Tag className="h-5 w-5 text-blue-600" />
          <h4 className="font-semibold text-gray-900">
            {templateConfig?.label || 'Template'} Details
          </h4>
        </div>

        {/* Template Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {getNameLabel()} *
          </label>
          <input
            type="text"
            value={formData.botName || ''}
            onChange={(e) => onInputChange('botName', e.target.value)}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
              errors.botName ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            placeholder={`Enter your ${getNameLabel().toLowerCase()}`}
          />
          {errors.botName && (
            <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {errors.botName}
            </p>
          )}
          <p className="mt-1.5 text-xs text-gray-500">
            This is the main name that will be displayed to visitors
          </p>
        </div>

        {/* Tagline (optional for most templates) */}
        {['profile', 'organisation', 'institution', 'ecommerce'].includes(templateType) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tagline
            </label>
            <input
              type="text"
              value={formData.tagline || ''}
              onChange={(e) => onInputChange('tagline', e.target.value)}
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                errors.tagline ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="A short, catchy phrase"
              maxLength={200}
            />
            {errors.tagline && (
              <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {errors.tagline}
              </p>
            )}
            <p className="mt-1.5 text-xs text-gray-500">
              Optional: A brief tagline or slogan (max 200 characters)
            </p>
          </div>
        )}

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {getDescriptionLabel()}
          </label>
          <textarea
            value={formData.description || ''}
            onChange={(e) => onInputChange('description', e.target.value)}
            rows={4}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none ${
              errors.description ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
            placeholder={`Describe your ${getDescriptionLabel().toLowerCase()}`}
            maxLength={1000}
          />
          {errors.description && (
            <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {errors.description}
            </p>
          )}
          <div className="flex items-center justify-between mt-1.5">
            <p className="text-xs text-gray-500">
              Provide a detailed description (max 1000 characters)
            </p>
            <span className="text-xs text-gray-400">
              {(formData.description || '').length}/1000
            </span>
          </div>
        </div>

        {/* Logo Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Logo
          </label>
          <FileUpload
            currentFile={formData.logoUrl}
            onFileSelect={(file) => onFileUpload(file, 'logoUrl')}
            onRemove={() => onInputChange('logoUrl', '')}
            accept="image/*"
            maxSize={5}
            uploadState={uploadStates.logoUrl}
            placeholder="Upload your logo"
            recommendedSize="Recommended: 400x400px, PNG or SVG"
          />
          {errors.logoUrl && (
            <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {errors.logoUrl}
            </p>
          )}
        </div>
      </div>

      {/* ========================================================================
          TEMPLATE CATEGORY SELECTION
      ======================================================================== */}
      {categoryConfig && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-6">
          <div className="flex items-center gap-2 pb-4 border-b border-gray-200">
            <Tag className="h-5 w-5 text-purple-600" />
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900">
                {categoryConfig.label} *
              </h4>
              <p className="text-sm text-gray-600 mt-0.5">
                {categoryConfig.description}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(categoryConfig.categories).map(([key, category]) => {
              const IconComponent = category.icon;
              const isSelected = currentCategory === key;

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleCategoryChange(key)}
                  className={`relative p-5 border-2 rounded-xl text-left transition-all hover:shadow-md ${
                    isSelected
                      ? 'border-current shadow-lg ring-2 ring-offset-2'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  style={{
                    borderColor: isSelected ? category.color : undefined,
                    backgroundColor: isSelected ? category.color + '08' : undefined,
                    ringColor: isSelected ? category.color + '40' : undefined
                  }}
                >
                  {/* Selection indicator */}
                  {isSelected && (
                    <div
                      className="absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: category.color }}
                    >
                      <CheckCircle className="h-4 w-4 text-white" />
                    </div>
                  )}

                  {/* Icon */}
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center mb-3"
                    style={{ backgroundColor: category.color + '20' }}
                  >
                    <IconComponent
                      className="h-6 w-6"
                      style={{ color: category.color }}
                    />
                  </div>

                  {/* Label */}
                  <h5 className="font-semibold text-gray-900 mb-1">
                    {category.label}
                  </h5>

                  {/* Description */}
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {category.description}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Category Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 mb-1">
                  Category Selection
                </p>
                <p className="text-sm text-blue-700">
                  Your category selection will determine available features and default settings. 
                  You can change this later, but some configurations may need adjustment.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================
          OWNERSHIP DETAILS SECTION
      ======================================================================== */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-6">
        <div className="flex items-center gap-2 pb-4 border-b border-gray-200">
          <User className="h-5 w-5 text-green-600" />
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900">Ownership Details</h4>
            <p className="text-sm text-gray-600 mt-0.5">
              Who owns or manages this {templateConfig?.label?.toLowerCase() || 'template'}?
            </p>
          </div>
        </div>

        {/* Ownership Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Ownership Type *
          </label>
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(OWNERSHIP_TYPES).map(([key, type]) => {
              const IconComponent = type.icon;
              const isSelected = activeOwnershipType === key;

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleOwnershipTypeChange(key)}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isSelected ? 'bg-blue-100' : 'bg-gray-100'
                      }`}
                    >
                      <IconComponent
                        className={`h-5 w-5 ${
                          isSelected ? 'text-blue-600' : 'text-gray-600'
                        }`}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {type.label}
                      </div>
                      <div className="text-xs text-gray-600 mt-0.5">
                        {type.description}
                      </div>
                    </div>
                    {isSelected && (
                      <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Individual Ownership Fields */}
        {activeOwnershipType === 'individual' && (
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Owner Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.ownerName || ''}
                  onChange={(e) => onInputChange('ownerName', e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Full name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  value={formData.ownerEmail || ''}
                  onChange={(e) => onInputChange('ownerEmail', e.target.value)}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.ownerEmail ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="email@example.com"
                />
              </div>
              {errors.ownerEmail && (
                <p className="mt-1.5 text-sm text-red-600">{errors.ownerEmail}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Phone
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="tel"
                  value={formData.ownerPhone || ''}
                  onChange={(e) => onInputChange('ownerPhone', e.target.value)}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.ownerPhone ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              {errors.ownerPhone && (
                <p className="mt-1.5 text-sm text-red-600">{errors.ownerPhone}</p>
              )}
            </div>
          </div>
        )}

        {/* Organisation Ownership Fields */}
        {activeOwnershipType === 'organisation' && (
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organisation Name
              </label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.organisationName || ''}
                  onChange={(e) => onInputChange('organisationName', e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Company or organization name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organisation Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  value={formData.organisationEmail || ''}
                  onChange={(e) => onInputChange('organisationEmail', e.target.value)}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.organisationEmail ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="contact@company.com"
                />
              </div>
              {errors.organisationEmail && (
                <p className="mt-1.5 text-sm text-red-600">{errors.organisationEmail}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organisation Phone
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="tel"
                  value={formData.organisationPhone || ''}
                  onChange={(e) => onInputChange('organisationPhone', e.target.value)}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.organisationPhone ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              {errors.organisationPhone && (
                <p className="mt-1.5 text-sm text-red-600">{errors.organisationPhone}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Organisation Website
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="url"
                  value={formData.organisationWebsite || ''}
                  onChange={(e) => onInputChange('organisationWebsite', e.target.value)}
                  className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.organisationWebsite ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="https://www.company.com"
                />
              </div>
              {errors.organisationWebsite && (
                <p className="mt-1.5 text-sm text-red-600">{errors.organisationWebsite}</p>
              )}
              <p className="mt-1.5 text-xs text-gray-500">
                Include full URL with https://
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Info className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h5 className="font-semibold text-gray-900 mb-2">
              Need Help Getting Started?
            </h5>
            <p className="text-sm text-gray-700 leading-relaxed mb-3">
              The basic information you provide here will be displayed prominently to your visitors. 
              Make sure to:
            </p>
            <ul className="text-sm text-gray-700 space-y-1.5">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Choose a clear, descriptive name that represents your content</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Select the category that best matches your use case</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Provide accurate contact information for visitor inquiries</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInfoTab;